import type {
  ConsentCookie,
  ConsentLevel,
  ConsentPreferences,
} from '@/types/cookie-consent';

/**
 * Type guard to check if a value is a valid ConsentLevel.
 * @param value - Value to check.
 * @returns True if value is a valid ConsentLevel.
 */
export function isConsentLevel(value: any): value is ConsentLevel {
  return (
    value === 'necessary' || value === 'analytical' || value === 'functional'
  );
}

/**
 * Type guard to check if a value conforms to the ConsentCookie interface.
 * @param value - Value to check.
 * @returns True if value is a valid ConsentCookie.
 */
export function isConsentCookie(value: any): value is ConsentCookie {
  if (!value || typeof value !== 'object') {
    return false;
  }

  // Check required fields
  if (typeof value.revision !== 'number') {
    return false;
  }
  if (typeof value.rfc_cookie !== 'boolean') {
    return false;
  }

  // Check optional arrays
  if (value.level !== undefined) {
    if (!Array.isArray(value.level) || !value.level.every(isConsentLevel)) {
      return false;
    }
  }

  if (value.accepted_categories !== undefined) {
    if (
      !Array.isArray(value.accepted_categories) ||
      !value.accepted_categories.every(isConsentLevel)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Type guard to check if a value conforms to the ConsentPreferences interface.
 * @param value - Value to check.
 * @returns True if value is a valid ConsentPreferences.
 */
export function isConsentPreferences(value: any): value is ConsentPreferences {
  if (!value || typeof value !== 'object') {
    return false;
  }

  // Check required fields
  if (
    value.accept_type !== 'all' &&
    value.accept_type !== 'custom' &&
    value.accept_type !== 'necessary'
  ) {
    return false;
  }
  if (
    !Array.isArray(value.accepted_categories) ||
    !value.accepted_categories.every(isConsentLevel)
  ) {
    return false;
  }
  if (
    !Array.isArray(value.rejected_categories) ||
    !value.rejected_categories.every(isConsentLevel)
  ) {
    return false;
  }

  return true;
}
